/**
 * Avatar component
 */
import classNames from 'classnames/bind';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { getCurrentUser } from '~/modules/currentUser';

import config from '~/config';

import memoFreezeImg, { frozenAvatars } from './freezing';
import styles from './index.scss';

const cx = classNames.bind(styles);

const DEFAULT_AVATAR = `${config.avatarsHost}/avatars/default`;

interface User {
  id: number;
  username: string;
  avatar: {
    url: string;
  };
}

interface AvatarProps {
  user?: User | null;
  userId?: number | null;
  username?: string | null;
  controlled?: boolean;
  hovering?: boolean;
  className?: string;
  size?: string | undefined;
  previewImg?: string;
}

interface DefaultAvatarProps {
  className?: string | undefined;
  size?: string;
}

/**
 * Avatar compoonent, allows freezing gif avatars
 * currently it has no way to tell if an avatar is actually a gif or not so it just assumes it's a gif
 */
const Avatar: React.FC<AvatarProps> = ({
  user = null,
  userId = null,
  username = null,
  controlled = false,
  hovering = false,
  className = '',
  size = undefined,
  previewImg = '',
  ...props
}) => {
  const currentUser = useSelector(getCurrentUser);
  const isStatic = user?.avatar?.url && !user.avatar.url?.endsWith('.gif');
  const isMyAvatar = currentUser && currentUser.username === username;

  const { data } = useQuery({
    queryKey: ['user', username],
    queryFn: async () => {
      if (!username) return null;
      const res = await axios.get(`${config.api}/users/${username}`, {
        params: {
          friendly: 1,
        }
      });
      return res.data?.avatar?.url;
    },
    enabled: !previewImg && !user?.avatar?.url && !isMyAvatar && !userId && !!username,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  const src = (() => {
    if (previewImg) {
      return previewImg;
    }

    if (isMyAvatar) {
      return `${config.avatarsHost}/avatars/${currentUser.id}`;
    }

    if (userId) {
      return `${config.avatarsHost}/avatars/${userId}`;
    }

    return data || `${config.api}/avatars/${username}`;
  })();

  const frozenUrl = frozenAvatars[src];

  const freeze = useCallback((e) => {
    memoFreezeImg(e.target);
  }, []);
  return (
    <div
      className={cx('Avatar', className, {
        'Avatar--Uncontrolled': !controlled,
        'Avatar--Controlled': controlled,
        'Avatar--Hovering': hovering,
      })}
      style={{
        '--size': size || undefined,
      } as React.CSSProperties}
      {...props}
    >
      {/* frozen img */}
      <img
        crossOrigin="anonymous"
        loading="lazy"
        className={cx('Avatar__Image', 'Avatar__Image--Frozen', 'dont-freeze')}
        alt={`${username}'s avatar`}
        src={frozenUrl || src}
      />
      {/* normal img */}
      <img
        crossOrigin="anonymous"
        loading="lazy"
        className={cx('Avatar__Image', 'Avatar__Image--NotFrozen', 'dont-freeze')}
        onLoad={isStatic ? undefined : freeze}
        alt={`${username}'s avatar`}
        src={src}
      />
    </div>
  );
};

export const DefaultAvatar: React.FC<DefaultAvatarProps> = ({
  className = '',
  size = undefined,
  ...props
}) => (
  <div
    className={cx('Avatar', className)}
    style={{
      '--size': size || undefined,
    } as React.CSSProperties}
    {...props}
  >
    <img
      crossOrigin="anonymous"
      loading="lazy"
      className={cx('Avatar__Image', 'Avatar__Image--Frozen')}
      alt="Default avatar"
      src={DEFAULT_AVATAR}
    />
  </div>
);

export default Avatar;
